import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError } from "@/api";
import logoUrl from "@/assets/ankhara-logo-mark.png";
import NotifyCapture from "@/components/NotifyCapture";
import { Flame, Heart, Skull, MessageCircle, Camera, Download, Share2, X, Link2, Facebook, Twitter, Instagram, Tag as TagIcon } from "lucide-react";

const track = (name, props = {}) => {
  try { window.posthog?.capture(name, props); } catch { /* noop */ }
  try { window.gtag?.("event", name, props); } catch { /* noop */ }
};

const RX = [
  { key: "flame", Icon: Flame },
  { key: "heart", Icon: Heart },
  { key: "skull", Icon: Skull },
];

const RX_KEY = "ank_rx_v1";
const loadRx = () => {
  try { return JSON.parse(localStorage.getItem(RX_KEY)) || {}; } catch { return {}; }
};

function Comments({ photo, onPosted }) {
  const [list, setList] = useState(null);
  const [text, setText] = useState("");
  const [handle, setHandle] = useState(() => localStorage.getItem("ank_handle") || "");
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/community/photos/${photo.id}/comments`)
      .then(({ data }) => setList(data))
      .catch(() => setList([]));
  }, [photo.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/community/photos/${photo.id}/comments`, {
        handle: handle.trim() || undefined,
        text,
        website: website || undefined,
      });
      setList((l) => [...(l || []), data]);
      setText("");
      if (handle.trim()) localStorage.setItem("ank_handle", handle.trim());
      onPosted(photo.id);
      track("community_comment", { session: photo.session });
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pol-comments" data-testid={`comments-${photo.id}`}>
      {list === null ? (
        <p className="pol-cmt-loading">READING THE WALL…</p>
      ) : list.length === 0 ? (
        <p className="pol-cmt-loading">NO WORDS YET — SAY SOMETHING.</p>
      ) : (
        list.map((c) => (
          <p key={c.id} className="pol-cmt" data-testid={`cmt-${c.id}`}>
            <b>{c.handle || "anonymous"}</b> {c.text}
          </p>
        ))
      )}
      <form className="pol-cmt-form" onSubmit={submit}>
        <input
          type="text"
          className="pol-cmt-handle"
          placeholder="@handle"
          value={handle}
          maxLength={30}
          onChange={(e) => setHandle(e.target.value)}
          data-testid={`cmt-handle-${photo.id}`}
          aria-label="Your handle"
        />
        <input
          type="text"
          className="pol-cmt-text"
          placeholder="leave a word…"
          value={text}
          maxLength={280}
          onChange={(e) => setText(e.target.value)}
          data-testid={`cmt-text-${photo.id}`}
          aria-label="Comment"
        />
        <input
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="pol-hp"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        <button type="submit" className="pol-cmt-send" disabled={busy || !text.trim()} data-testid={`cmt-send-${photo.id}`}>
          {busy ? "…" : "POST"}
        </button>
      </form>
      {err && <p className="pol-cmt-err" data-testid={`cmt-error-${photo.id}`}>{err}</p>}
    </div>
  );
}

function Polaroid({ p, mine, onReact, onCommentPosted, onShare, onDownloadMenu, onTag, onReport, onTagSearch }) {
  const [open, setOpen] = useState(false);
  const ratio = p.width && p.height ? `${p.width} / ${p.height}` : undefined;
  return (
    <motion.article
      className="pol"
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      data-testid={`pol-${p.id}`}
    >
      <div className="pol-media" style={ratio ? { aspectRatio: ratio } : undefined}>
        {p.media_type === "video" ? (
          <video src={p.file_url} poster={p.display_url || p.thumb_url} controls preload="metadata" playsInline
            className="pol-video" data-testid={`pol-video-${p.id}`} />
        ) : (
          <img src={p.display_url || p.thumb_url} alt={`ANKHARA community frame by ${p.handle || "a dancer"} — session ${p.session}`} loading="lazy"
            onError={(e) => {
              const el = e.currentTarget;
              if (!el.dataset.fb) { el.dataset.fb = "1"; el.src = p.thumb_url; }
              else if (el.dataset.fb === "1") { el.dataset.fb = "2"; el.src = p.file_url; }
            }} />
        )}
        <div className="pol-grain" aria-hidden="true" />
        <span className="pol-stamp">ANKHARA</span>
        {p.source === "photographer" && <span className="pol-pro">PRO</span>}
        {p.is_featured && <span className="pol-featured" data-testid={`featured-badge-${p.id}`}>FEATURED</span>}
        {p.is_potn && <span className="pol-potn" data-testid={`potn-badge-${p.id}`}>PHOTO OF THE NIGHT</span>}
      </div>
      <div className="pol-caption">
        <span className="pol-handle" data-testid={`pol-handle-${p.id}`}>{p.handle || "anonymous"}</span>
        <span className="pol-meta">SESSION {p.session} · {(p.created_at || "").slice(0, 10)}</span>
      </div>
      {(p.tags?.length || 0) > 0 && (
        <div className="pol-tags" data-testid={`pol-tags-${p.id}`}>
          {p.tags.map((t) => (
            <button key={t} type="button" className="pol-tag" onClick={() => onTagSearch(t)}
              data-testid={`pol-tag-${p.id}-${t}`}>{t}</button>
          ))}
        </div>
      )}
      <div className="pol-rx">
        {RX.map(({ key, Icon }) => (
          <motion.button
            key={key}
            type="button"
            whileTap={{ scale: 1.4 }}
            className={`pol-rx-btn ${mine?.[key] ? "on" : ""}`}
            onClick={() => onReact(p, key)}
            aria-label={`${key} reaction`}
            aria-pressed={!!mine?.[key]}
            data-testid={`rx-${key}-${p.id}`}
          >
            <Icon size={17} fill={mine?.[key] ? "currentColor" : "none"} />
            <span data-testid={`rx-count-${key}-${p.id}`}>{p.reactions?.[key] || 0}</span>
          </motion.button>
        ))}
        <button
          type="button"
          className={`pol-rx-btn ${open ? "on" : ""}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Comments"
          aria-expanded={open}
          data-testid={`comments-toggle-${p.id}`}
        >
          <MessageCircle size={17} />
          <span data-testid={`comments-count-${p.id}`}>{p.comments_count || 0}</span>
        </button>
        <button
          type="button"
          className="pol-rx-btn"
          onClick={() => onTag(p)}
          aria-label="Tag yourself or a friend"
          title="Tag yourself or a friend"
          data-testid={`tag-${p.id}`}
        >
          <TagIcon size={17} />
        </button>
        <button
          type="button"
          className="pol-rx-btn"
          onClick={() => onShare(p)}
          aria-label="Share this frame"
          data-testid={`share-${p.id}`}
        >
          <Share2 size={17} />
        </button>
        <button
          type="button"
          className="pol-rx-btn pol-dl"
          onClick={() => onDownloadMenu(p)}
          aria-label="Download options"
          title="Download options"
          data-testid={`download-${p.id}`}
        >
          <Download size={17} />
        </button>
      </div>
      <button type="button" className="pol-report" onClick={() => onReport(p)} data-testid={`report-${p.id}`}>
        remove my tag / report
      </button>
      {open && <Comments photo={p} onPosted={onCommentPosted} />}
    </motion.article>
  );
}

function TagSheet({ photo, onClose, onTagged }) {
  const [name, setName] = useState(() => localStorage.getItem("ank_handle") || "");
  const [isSelf, setIsSelf] = useState(true);
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [doneOk, setDoneOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/community/photos/${photo.id}/tags`, {
        name: name.trim(),
        is_self: isSelf,
        email: isSelf && email.trim() ? email.trim() : undefined,
        website: website || undefined,
      });
      localStorage.setItem("ank_handle", name.trim());
      track("photo_tag", { session: photo.session });
      onTagged(photo.id, data.tags);
      setDoneOk(true);
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-veil" onClick={onClose} data-testid="tag-sheet">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="sheet-x" onClick={onClose} aria-label="Close" data-testid="tag-sheet-close"><X size={18} /></button>
        {doneOk ? (
          <>
            <p className="sheet-title">TAGGED</p>
            <p className="sheet-note">Name's on the frame. Find it anytime with the search up top.</p>
            <button type="button" className="sheet-btn sheet-btn-acid" onClick={onClose} data-testid="tag-done-btn">DONE</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <p className="sheet-title">TAG YOURSELF / A FRIEND</p>
            <input type="text" className="sheet-input" placeholder="@handle or name" value={name} maxLength={40}
              onChange={(e) => setName(e.target.value)} data-testid="tag-name-input" aria-label="Handle or name" />
            <label className="sheet-check">
              <input type="checkbox" checked={isSelf} onChange={(e) => setIsSelf(e.target.checked)} data-testid="tag-self-check" />
              <span>That's me in this photo</span>
            </label>
            {isSelf && (
              <>
                <input type="email" className="sheet-input" placeholder="your@email.com (optional — get notified)"
                  value={email} onChange={(e) => setEmail(e.target.value)} data-testid="tag-email-input" aria-label="Your email (optional)" />
                <p className="sheet-note">Only your own email, only when it's you — we never email addresses typed for friends.</p>
              </>
            )}
            <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} className="pol-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            {err && <p className="notify-err" data-testid="tag-error">{err}</p>}
            <button type="submit" className="sheet-btn sheet-btn-acid sheet-full" disabled={busy || !name.trim()} data-testid="tag-submit">
              {busy ? "TAGGING…" : "ADD TAG"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ReportSheet({ photo, onClose }) {
  const [tag, setTag] = useState("");
  const [reason, setReason] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/community/photos/${photo.id}/report`, { reason: reason || undefined, tag: tag || undefined });
      track("photo_report", { session: photo.session });
      setSent(true);
    } catch { /* noop */ } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-veil" onClick={onClose} data-testid="report-sheet">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="sheet-x" onClick={onClose} aria-label="Close" data-testid="report-sheet-close"><X size={18} /></button>
        {sent ? (
          <>
            <p className="sheet-title">FLAGGED FOR REVIEW</p>
            <p className="sheet-note">The organizers got it. Tags and photos come down fast when asked.</p>
            <button type="button" className="sheet-btn sheet-btn-acid" onClick={onClose} data-testid="report-done-btn">DONE</button>
          </>
        ) : (
          <>
            <p className="sheet-title">REMOVE MY TAG / REPORT</p>
            {(photo.tags?.length || 0) > 0 && (
              <select className="sheet-input" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="report-tag-select" aria-label="Which tag">
                <option value="">Report the photo itself</option>
                {photo.tags.map((t) => <option key={t} value={t}>Remove tag: {t}</option>)}
              </select>
            )}
            <input type="text" className="sheet-input" placeholder="why? (optional)" value={reason} maxLength={280}
              onChange={(e) => setReason(e.target.value)} data-testid="report-reason-input" aria-label="Reason (optional)" />
            <button type="button" className="sheet-btn sheet-btn-acid sheet-full" onClick={submit} disabled={busy} data-testid="report-submit">
              {busy ? "SENDING…" : "FLAG FOR REMOVAL"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function useMiniCountdown(iso) {
  const [label, setLabel] = useState("");
  useEffect(() => {
    if (!iso) { setLabel(""); return undefined; }
    const target = new Date(iso).getTime();
    const tick = () => {
      const d = target - Date.now();
      if (d <= 0) { setLabel(""); return; }
      const days = Math.floor(d / 86400000);
      const hrs = Math.floor(d / 3600000) % 24;
      const min = Math.floor(d / 60000) % 60;
      setLabel(days > 0 ? `T—${days}D ${hrs}H` : `T—${hrs}H ${min}M`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [iso]);
  return label;
}

function RsvpStamp({ ev, testid }) {
  if (!ev?.rsvpUrl) return null;
  return (
    <a
      className="com-rsvp-stamp"
      href={ev.rsvpUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => track("rsvp_click", { event_category: "engagement", event_label: `sessions${(ev?.title || "").match(/\d{3}/)?.[0] || "current"}_feed` })}
      data-testid={testid}
    >
      {ev.rsvpLabel || "RSVP"} ↗
    </a>
  );
}

function RsvpMeta({ ev }) {
  const cd = useMiniCountdown(ev?.startIso);
  const going = parseInt(ev?.rsvp_count, 10) || 0;
  return (
    <>
      {cd && <span className="com-rsvp-cd" data-testid="feed-countdown">{cd}</span>}
      {going > 0 && <span className="com-rsvp-going" data-testid="feed-going">{going} GOING</span>}
    </>
  );
}

function RsvpInline({ ev }) {
  if (!ev) return null;
  return (
    <div className="com-rsvp-inline" data-testid="feed-rsvp-inline">
      <p className="com-rsvp-inline-title">NEXT SESSION</p>
      <p className="com-rsvp-inline-meta">{ev.title?.replace("ANKHARA SESSIONS: ", "SESSION ")} · {ev.date}{ev.time ? ` · ${ev.time}` : ""}</p>
      <p className="com-rsvp-inline-venue">{ev.venue}</p>
      <p className="com-rsvp-extras"><RsvpMeta ev={ev} /></p>
      <RsvpStamp ev={ev} testid="feed-rsvp-inline-btn" />
    </div>
  );
}

function DownloadSheet({ photo, onClose }) {
  const base = `/api/community/photos/${photo.id}/export`;
  return (
    <div className="sheet-veil" onClick={onClose} data-testid="download-sheet">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="sheet-x" onClick={onClose} aria-label="Close" data-testid="download-sheet-close"><X size={18} /></button>
        <p className="sheet-title">DOWNLOAD THIS FRAME</p>
        <img src={`${base}?variant=polaroid`} alt="Polaroid preview" className="sheet-preview" data-testid="polaroid-preview" loading="lazy" />
        <div className="sheet-row">
          <a href={`${base}?variant=polaroid`} download={`ankhara-s${photo.session}-polaroid.jpg`}
            className="sheet-btn sheet-btn-acid" data-testid="download-polaroid"
            onClick={() => track("photo_download", { variant: "polaroid", session: photo.session })}>
            POLAROID ↓
          </a>
          <a href={`${base}?variant=clean`} download={`ankhara-s${photo.session}-clean.jpg`}
            className="sheet-btn" data-testid="download-clean"
            onClick={() => track("photo_download", { variant: "clean", session: photo.session })}>
            CLEAN ↓
          </a>
        </div>
        <p className="sheet-note">POLAROID: framed with the ANKHARA strip + session caption. CLEAN: full quality with a small corner mark.</p>
      </div>
    </div>
  );
}

function ShareSheet({ photo, onClose, onDownload }) {
  const [copied, setCopied] = useState(false);
  const pageUrl = `${window.location.origin}/api/community/photo/${photo.id}`;
  const imgUrl = `${window.location.origin}/api/community/photos/${photo.id}/export?variant=polaroid`;
  const desc = `ANKHARA Session ${photo.session} — shot by ${photo.handle || "the community"}`;
  const go = (network, url) => {
    track("photo_share", { network, session: photo.session });
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="sheet-veil" onClick={onClose} data-testid="share-sheet">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="sheet-x" onClick={onClose} aria-label="Close" data-testid="share-sheet-close"><X size={18} /></button>
        <p className="sheet-title">SHARE THIS FRAME</p>
        <div className="sheet-grid">
          <button type="button" className="sheet-btn" data-testid="share-copy"
            onClick={async () => {
              try { await navigator.clipboard.writeText(pageUrl); setCopied(true); } catch { /* noop */ }
              track("photo_share", { network: "copy_link", session: photo.session });
            }}>
            <Link2 size={15} /> {copied ? "COPIED" : "COPY LINK"}
          </button>
          <button type="button" className="sheet-btn" data-testid="share-facebook"
            onClick={() => go("facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`)}>
            <Facebook size={15} /> FACEBOOK
          </button>
          <button type="button" className="sheet-btn" data-testid="share-pinterest"
            onClick={() => go("pinterest", `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(pageUrl)}&media=${encodeURIComponent(imgUrl)}&description=${encodeURIComponent(desc)}`)}>
            <span className="sheet-pi">P</span> PINTEREST
          </button>
          <button type="button" className="sheet-btn" data-testid="share-x"
            onClick={() => go("x", `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(desc)}`)}>
            <Twitter size={15} /> X
          </button>
          <button type="button" className="sheet-btn sheet-btn-acid" data-testid="share-download"
            onClick={() => { onClose(); onDownload(); }}>
            <Download size={15} /> DOWNLOAD
          </button>
        </div>
        <p className="sheet-note"><Instagram size={12} style={{ verticalAlign: -2 }} /> Instagram / TikTok don't accept web share links — download the frame, then post it.</p>
      </div>
    </div>
  );
}

export default function Community() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [session, setSession] = useState("");
  const [sessions, setSessions] = useState([]);
  const [uploadUrl, setUploadUrl] = useState(null);
  const [rxMine, setRxMine] = useState(loadRx);
  const [ev, setEv] = useState(null);
  const [dlFor, setDlFor] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const [tagFor, setTagFor] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const [tagFilter, setTagFilter] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [params] = useSearchParams();
  const targetPhoto = params.get("photo");
  const scrolledTo = useRef(false);
  const sentinel = useRef(null);

  const loadFirst = useCallback(async (sess, tag) => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/feed", {
        params: { limit: 12, ...(sess ? { session: sess } : {}), ...(tag ? { tag } : {}) },
      });
      setItems([...(data.potn ? [data.potn] : []), ...(data.featured || []), ...data.items]);
      setCursor(data.next);
      setHasMore(data.has_more);
      track("community_view", { session: sess || "all", tag: tag || undefined });
    } catch { /* noop */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFirst(session, tagFilter); }, [session, tagFilter, loadFirst]);

  useEffect(() => {
    api.get("/community/sessions").then(({ data }) => setSessions(data)).catch(() => {});
    api.get("/community/upload-link").then(({ data }) => setUploadUrl(data.url)).catch(() => {});
    api.get("/site-config").then(({ data }) => setEv(data.event)).catch(() => {});
  }, []);

  // deep link: /community?photo=<id> scrolls to the shared frame
  useEffect(() => {
    if (!targetPhoto || scrolledTo.current || loading) return;
    const el = document.querySelector(`[data-testid="pol-${targetPhoto}"]`);
    if (el) {
      scrolledTo.current = true;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.classList.add("pol-flash");
      setTimeout(() => el.classList.remove("pol-flash"), 2600);
    }
  }, [targetPhoto, items, loading]);

  const sharePhoto = async (p) => {
    const url = `${window.location.origin}/api/community/photo/${p.id}`;
    if (navigator.canShare) {
      try {
        const blob = await (await fetch(`/api/community/photos/${p.id}/export?variant=polaroid`)).blob();
        const file = new File([blob], `ankhara-session-${p.session}.jpg`, { type: "image/jpeg" });
        if (navigator.canShare({ files: [file] })) {
          track("photo_share", { network: "native", session: p.session });
          await navigator.share({ files: [file], title: "ANKHARA", text: `ANKHARA Session ${p.session}`, url });
          return;
        }
      } catch (e) {
        if (e?.name === "AbortError") return; // user closed the sheet
      }
    }
    setShareFor(p); // desktop / unsupported → menu
  };

  const loadMore = useCallback(async () => {
    if (!hasMore || !cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/community/feed", {
        params: { before: cursor, limit: 12, ...(session ? { session } : {}), ...(tagFilter ? { tag: tagFilter } : {}) },
      });
      setItems((it) => [...it, ...data.items]);
      setCursor(data.next);
      setHasMore(data.has_more);
    } catch { /* noop */ } finally {
      setLoadingMore(false);
    }
  }, [cursor, hasMore, loadingMore, session, tagFilter]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver((es) => {
      if (es[0].isIntersecting) loadMore();
    }, { rootMargin: "600px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const react = async (p, type) => {
    const mine = rxMine[p.id]?.[type];
    const delta = mine ? -1 : 1;
    setItems((its) => its.map((x) => (x.id === p.id
      ? { ...x, reactions: { ...x.reactions, [type]: Math.max(0, (x.reactions?.[type] || 0) + delta) } }
      : x)));
    try {
      const { data } = await api.post(`/community/photos/${p.id}/react`, { type, delta });
      setItems((its) => its.map((x) => (x.id === p.id ? { ...x, reactions: data.reactions } : x)));
      const next = { ...rxMine, [p.id]: { ...(rxMine[p.id] || {}), [type]: !mine } };
      setRxMine(next);
      localStorage.setItem(RX_KEY, JSON.stringify(next));
      if (!mine) track("community_react", { type, session: p.session });
    } catch {
      setItems((its) => its.map((x) => (x.id === p.id
        ? { ...x, reactions: { ...x.reactions, [type]: Math.max(0, (x.reactions?.[type] || 0) - delta) } }
        : x)));
    }
  };

  const onCommentPosted = (pid) => {
    setItems((its) => its.map((x) => (x.id === pid ? { ...x, comments_count: (x.comments_count || 0) + 1 } : x)));
  };

  return (
    <div className="com-page" data-testid="community-page">
      <div className="com-noise" aria-hidden="true" />
      <header className="com-head">
        <div className="com-head-in">
          <a href="/" data-testid="community-home-link"><img src={logoUrl} alt="ANKHARA" className="com-logo" /></a>
          <span className="com-title" data-testid="community-title">THE FEED</span>
          {uploadUrl && (
            <a href={uploadUrl} className="com-add" data-testid="community-upload-cta"
              onClick={() => track("community_upload_click")}>
              <Camera size={15} style={{ marginRight: 6, verticalAlign: -2 }} />ADD YOURS
            </a>
          )}
        </div>
        {sessions.length > 0 && (
          <div className="com-sessions" data-testid="community-sessions">
            <button type="button" className={`ank-chip ${session === "" ? "on" : ""}`}
              onClick={() => setSession("")} data-testid="community-chip-all">ALL</button>
            {sessions.map((s) => (
              <button key={s} type="button" className={`ank-chip ${session === s ? "on" : ""}`}
                onClick={() => setSession(s)} data-testid={`community-chip-${s}`}>SESSION {s}</button>
            ))}
          </div>
        )}
        <div className="com-tagsearch" data-testid="tag-search-row">
          <input
            type="text"
            className="com-tagsearch-input"
            placeholder="find your frame — @handle or name"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setTagFilter(tagInput.trim()); track("tag_search"); } }}
            data-testid="tag-search-input"
            aria-label="Find photos by handle or name"
          />
          {tagFilter ? (
            <button type="button" className="ank-chip on" onClick={() => { setTagFilter(""); setTagInput(""); }}
              data-testid="tag-search-clear">TAG: {tagFilter} ×</button>
          ) : (
            <button type="button" className="ank-chip com-tagsearch-go" onClick={() => { setTagFilter(tagInput.trim()); track("tag_search"); }}
              data-testid="tag-search-btn">FIND</button>
          )}
        </div>
      </header>

      <main className="com-feed" data-testid="community-feed">
        {loading ? (
          <p className="com-loader" data-testid="community-loading">LOADING ROLL…</p>
        ) : items.length === 0 ? (
          <div className="com-empty" data-testid="community-empty">
            <p className="com-empty-title">THE ROLL IS BLANK</p>
            <p className="com-empty-sub">No frames from the floor yet. Shoot the next session and be the first on the wall.</p>
            {uploadUrl && (
              <a href={uploadUrl} className="com-add com-add-big" data-testid="community-empty-cta">ADD YOURS</a>
            )}
          </div>
        ) : (
          <>
            {items.map((p, i) => (
              <Fragment key={p.id}>
                <Polaroid p={p} mine={rxMine[p.id]} onReact={react} onCommentPosted={onCommentPosted}
                  onShare={sharePhoto} onDownloadMenu={setDlFor}
                  onTag={setTagFor} onReport={setReportFor}
                  onTagSearch={(t) => { setTagFilter(t); setTagInput(t); }} />
                {i === 5 && !tagFilter && (
                  <div className="com-notify-inline" data-testid="feed-notify-inline"><NotifyCapture source="feed_inline" testid="feed-notify" /></div>
                )}
                {(i + 1) % 12 === 0 && i < items.length - 1 && <RsvpInline ev={ev} />}
              </Fragment>
            ))}
            <div ref={sentinel} className="com-sentinel" aria-hidden="true" />
            {loadingMore && <p className="com-loader" data-testid="community-loading-more">LOADING ROLL…</p>}
            {!hasMore && items.length >= 12 && (
              <p className="com-end" data-testid="community-end">— END OF THE ROLL —</p>
            )}
          </>
        )}
      </main>

      {ev?.rsvpUrl && (
        <div className="com-rsvp-bar" data-testid="feed-rsvp-bar">
          <span className="com-rsvp-info">
            {ev.title?.replace("ANKHARA SESSIONS: ", "SESSION ")} · {ev.date}{ev.time ? ` ${ev.time}` : ""} · {(ev.venue || "").split("—")[0].trim()}
          </span>
          <RsvpMeta ev={ev} />
          <RsvpStamp ev={ev} testid="feed-rsvp-bar-btn" />
        </div>
      )}
      {ev?.rsvpUrl && (
        <div className="com-rsvp-card" data-testid="feed-rsvp-card">
          <p className="com-rsvp-inline-title">NEXT SESSION</p>
          <p className="com-rsvp-inline-meta">{ev.title?.replace("ANKHARA SESSIONS: ", "SESSION ")}</p>
          <p className="com-rsvp-inline-venue">{ev.date}{ev.time ? ` · ${ev.time}` : ""}<br />{ev.venue}</p>
          <p className="com-rsvp-extras"><RsvpMeta ev={ev} /></p>
          <RsvpStamp ev={ev} testid="feed-rsvp-card-btn" />
        </div>
      )}

      {dlFor && <DownloadSheet photo={dlFor} onClose={() => setDlFor(null)} />}
      {shareFor && (
        <ShareSheet photo={shareFor} onClose={() => setShareFor(null)}
          onDownload={() => setDlFor(shareFor)} />
      )}
      {tagFor && (
        <TagSheet photo={tagFor} onClose={() => setTagFor(null)}
          onTagged={(pid, tags) => setItems((its) => its.map((x) => (x.id === pid ? { ...x, tags } : x)))} />
      )}
      {reportFor && <ReportSheet photo={reportFor} onClose={() => setReportFor(null)} />}
    </div>
  );
}
